import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { normalizeActiveModules } from './companyActiveModules';

export const MODULE_REQUEST_STATUS = {
  PENDING: 'pending',
  GRANTED: 'granted',
  REJECTED: 'rejected',
};

/** Platform-wide alert targeting for Super Admin inbox queries. */
export const SYSTEM_ALERT_COMPANY_ID = 'SYSTEM';

/**
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} companyId
 * @param {string} [fallbackUid]
 * @returns {Promise<string|null>}
 */
async function findCompanyOwnerUid(db, companyId, fallbackUid) {
  if (!companyId) return fallbackUid || null;
  const snap = await getDocs(
    query(collection(db, 'users'), where('companyId', '==', companyId)),
  );
  const admin = snap.docs.find((d) => d.data().role === 'company_admin');
  return admin?.id || fallbackUid || null;
}

/**
 * @param {object} opts
 * @returns {object}
 */
function buildModuleRequestTicket({
  companyId,
  companyName,
  moduleKey,
  moduleLabel,
  moduleRequestId,
  requestedByUid,
  requestedByEmail,
  ownerUid,
}) {
  const assigneeUid = ownerUid || requestedByUid || null;
  const assignedUsers = [...new Set([assigneeUid, requestedByUid].filter(Boolean))];

  return {
    companyId,
    title: `Module access request: ${moduleLabel}`,
    body: [
      `${requestedByEmail || 'A workspace admin'} requested access to "${moduleLabel}"`,
      `for ${companyName || 'this workspace'}.`,
      '',
      `Module: ${moduleLabel} (${moduleKey})`,
      `Linked request: ${moduleRequestId}`,
      '',
      'Your platform team will review this request. You can track progress in Tickets.',
    ].join('\n'),
    status: 'open',
    priority: 'medium',
    labelIds: [],
    assigneeUid,
    assignedUsers,
    platformEscalation: true,
    source: 'module_request',
    moduleRequestId,
    moduleKey,
    moduleLabel,
    archived: false,
    archivedAt: null,
    reporterUid: requestedByUid || null,
    aiEnriched: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: requestedByUid || null,
    updatedBy: requestedByUid || null,
  };
}

/**
 * @param {object} opts
 * @returns {object}
 */
function buildSuperAdminModuleRequestAlert({
  moduleLabel,
  companyName,
  companyId,
  moduleKey,
  moduleRequestId,
  ticketId,
  requestedByEmail,
  requestedByUid,
}) {
  return {
    title: `New Module Request: ${moduleLabel} from ${companyName || companyId}`,
    message: `${requestedByEmail || 'A company admin'} requested access to ${moduleLabel}. Review in Escalations or Companies → Module access requests.`,
    severity: 'warning',
    status: 'active',
    targetType: 'role',
    targetCompanyId: SYSTEM_ALERT_COMPANY_ID,
    targetRole: 'super_admin',
    ruleCode: 'MODULE_ACCESS_REQUESTED',
    createdAt: serverTimestamp(),
    startsAt: serverTimestamp(),
    expiresAt: null,
    createdByUid: requestedByUid || null,
    createdByEmail: requestedByEmail || '',
    readBy: {},
    dismissedBy: {},
    meta: {
      ticketId,
      moduleRequestId,
      companyId,
      moduleKey,
      moduleLabel,
      source: 'module_request',
      platform: true,
    },
  };
}

/**
 * @param {object} opts
 * @returns {object}
 */
function buildCompanyModuleRequestAlert({
  moduleLabel,
  companyId,
  moduleRequestId,
  ticketId,
  requestedByEmail,
  requestedByUid,
}) {
  return {
    title: `Module access requested: ${moduleLabel}`,
    message: 'A tracking ticket was opened for your workspace owner.',
    severity: 'info',
    status: 'active',
    targetType: 'company',
    targetCompanyId: companyId,
    targetRole: null,
    ruleCode: 'MODULE_ACCESS_REQUESTED',
    createdAt: serverTimestamp(),
    startsAt: serverTimestamp(),
    expiresAt: null,
    createdByUid: requestedByUid || null,
    createdByEmail: requestedByEmail || '',
    readBy: {},
    dismissedBy: {},
    meta: {
      ticketId,
      moduleRequestId,
      source: 'module_request',
    },
  };
}

/**
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} companyId
 * @param {string} moduleKey
 * @returns {Promise<boolean>}
 */
export async function hasPendingModuleRequest(db, companyId, moduleKey) {
  if (!companyId || !moduleKey) return false;
  const snap = await getDocs(
    query(
      collection(db, 'moduleRequests'),
      where('companyId', '==', companyId),
      where('moduleKey', '==', moduleKey),
      where('status', '==', MODULE_REQUEST_STATUS.PENDING),
    ),
  );
  return !snap.empty;
}

/**
 * @param {import('firebase/firestore').Firestore} db
 * @param {object} opts
 * @returns {Promise<{ requestId: string, ticketId: string }>}
 */
export async function createModuleRequest(db, {
  companyId,
  moduleKey,
  moduleLabel,
  companyName,
  requestedByUid,
  requestedByEmail,
  requestedByRole,
}) {
  const alreadyPending = await hasPendingModuleRequest(db, companyId, moduleKey);
  if (alreadyPending) {
    throw new Error('A request for this module is already pending.');
  }

  const ownerUid = await findCompanyOwnerUid(db, companyId, requestedByUid);
  const requestRef = doc(collection(db, 'moduleRequests'));
  const ticketRef = doc(collection(db, 'tickets'));
  const superAdminAlertRef = doc(collection(db, 'alerts'));
  const companyAlertRef = doc(collection(db, 'alerts'));
  const label = moduleLabel || moduleKey;
  const name = companyName || companyId;

  const batch = writeBatch(db);
  batch.set(requestRef, {
    companyId,
    moduleKey,
    moduleLabel: label,
    companyName: name,
    status: MODULE_REQUEST_STATUS.PENDING,
    requestedByUid: requestedByUid || null,
    requestedByEmail: requestedByEmail || '',
    requestedByRole: requestedByRole || '',
    ticketId: ticketRef.id,
    ownerUid: ownerUid || null,
    createdAt: serverTimestamp(),
    resolvedAt: null,
    resolvedByUid: null,
    resolvedByEmail: null,
  });
  batch.set(
    ticketRef,
    buildModuleRequestTicket({
      companyId,
      companyName: name,
      moduleKey,
      moduleLabel: label,
      moduleRequestId: requestRef.id,
      requestedByUid,
      requestedByEmail,
      ownerUid,
    }),
  );
  batch.set(
    superAdminAlertRef,
    buildSuperAdminModuleRequestAlert({
      moduleLabel: label,
      companyName: name,
      companyId,
      moduleKey,
      moduleRequestId: requestRef.id,
      ticketId: ticketRef.id,
      requestedByEmail,
      requestedByUid,
    }),
  );
  batch.set(
    companyAlertRef,
    buildCompanyModuleRequestAlert({
      moduleLabel: label,
      companyId,
      moduleRequestId: requestRef.id,
      ticketId: ticketRef.id,
      requestedByEmail,
      requestedByUid,
    }),
  );
  await batch.commit();

  return { requestId: requestRef.id, ticketId: ticketRef.id };
}

/**
 * Resolve a module request from its linked ticket (Super Admin action).
 * @param {import('firebase/firestore').Firestore} db
 * @param {object} ticket
 * @param {'granted'|'rejected'} decision
 * @param {{ uid?: string, email?: string }} resolver
 */
export async function resolveModuleRequestFromTicket(db, ticket, decision, resolver) {
  const moduleRequestId = ticket?.moduleRequestId;
  if (!moduleRequestId) {
    throw new Error('This ticket is not linked to a module request.');
  }

  const requestSnap = await getDoc(doc(db, 'moduleRequests', moduleRequestId));
  if (!requestSnap.exists()) {
    throw new Error('Module request not found.');
  }
  const request = { id: requestSnap.id, ...requestSnap.data() };
  if (request.status !== MODULE_REQUEST_STATUS.PENDING) {
    throw new Error('This module request has already been resolved.');
  }

  const moduleKey = ticket.moduleKey || request.moduleKey;
  const status = decision === 'granted'
    ? MODULE_REQUEST_STATUS.GRANTED
    : MODULE_REQUEST_STATUS.REJECTED;
  const note = decision === 'granted'
    ? 'Module access was approved by Super Admin.'
    : 'Module access request was rejected by Super Admin.';
  const existingBody = typeof ticket.body === 'string' ? ticket.body : '';

  const batch = writeBatch(db);

  batch.update(doc(db, 'moduleRequests', moduleRequestId), {
    status,
    resolvedAt: serverTimestamp(),
    resolvedByUid: resolver?.uid || null,
    resolvedByEmail: resolver?.email || '',
  });

  if (decision === 'granted') {
    const companySnap = await getDoc(doc(db, 'companies', ticket.companyId));
    if (!companySnap.exists()) {
      throw new Error('Company not found.');
    }
    const nextModules = {
      ...normalizeActiveModules(companySnap.data()?.activeModules),
      [moduleKey]: true,
    };
    batch.update(doc(db, 'companies', ticket.companyId), { activeModules: nextModules });
  }

  batch.update(doc(db, 'tickets', ticket.id), {
    status: decision === 'granted' ? 'resolved' : 'closed',
    moduleRequestStatus: status,
    body: `${existingBody}\n\n---\n${note}`,
    isAssignedToAi: false,
    assigneeAgentId: null,
    updatedAt: serverTimestamp(),
    updatedBy: resolver?.uid || null,
    resolvedAt: serverTimestamp(),
    resolvedByUid: resolver?.uid || null,
  });

  await batch.commit();

  return { request, status, note, moduleKey };
}

/**
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} ticketId
 * @param {'granted'|'rejected'} status
 * @param {{ uid?: string, email?: string }} resolver
 */
async function syncModuleRequestTicket(db, ticketId, status, resolver) {
  if (!ticketId) return;
  const ticketRef = doc(db, 'tickets', ticketId);
  const ticketSnap = await getDoc(ticketRef);
  if (!ticketSnap.exists()) return;

  const note = status === MODULE_REQUEST_STATUS.GRANTED
    ? 'Module access was granted by the platform team.'
    : 'Module access request was rejected by the platform team.';
  const existingBody = typeof ticketSnap.data().body === 'string' ? ticketSnap.data().body : '';

  await updateDoc(ticketRef, {
    status: status === MODULE_REQUEST_STATUS.GRANTED ? 'resolved' : 'closed',
    moduleRequestStatus: status,
    body: `${existingBody}\n\n---\n${note}`,
    isAssignedToAi: false,
    assigneeAgentId: null,
    updatedAt: serverTimestamp(),
    updatedBy: resolver?.uid || 'system:module-request',
    resolvedAt: serverTimestamp(),
    resolvedByUid: resolver?.uid || null,
  });
}

/**
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} companyId
 * @param {string} moduleKey
 * @param {'granted'|'rejected'} status
 * @param {{ uid?: string, email?: string }} resolver
 */
export async function closePendingModuleRequests(db, companyId, moduleKey, status, resolver) {
  const snap = await getDocs(
    query(
      collection(db, 'moduleRequests'),
      where('companyId', '==', companyId),
      where('moduleKey', '==', moduleKey),
      where('status', '==', MODULE_REQUEST_STATUS.PENDING),
    ),
  );
  if (snap.empty) return;

  const batch = writeBatch(db);
  const ticketIds = new Set();

  snap.docs.forEach((d) => {
    const data = d.data() || {};
    batch.update(d.ref, {
      status,
      resolvedAt: serverTimestamp(),
      resolvedByUid: resolver?.uid || null,
      resolvedByEmail: resolver?.email || '',
    });
    if (data.ticketId) ticketIds.add(data.ticketId);
  });
  await batch.commit();

  await Promise.all(
    [...ticketIds].map((ticketId) => syncModuleRequestTicket(db, ticketId, status, resolver)),
  );
}

/**
 * Grant a pending module request and enable the module on the company.
 * @param {import('firebase/firestore').Firestore} db
 * @param {{ id: string, companyId: string, moduleKey: string, ticketId?: string }} request
 * @param {{ uid?: string, email?: string }} resolver
 */
export async function grantModuleRequest(db, request, resolver) {
  if (request.ticketId) {
    const ticketSnap = await getDoc(doc(db, 'tickets', request.ticketId));
    if (ticketSnap.exists()) {
      await resolveModuleRequestFromTicket(
        db,
        { id: ticketSnap.id, ...ticketSnap.data() },
        'granted',
        resolver,
      );
      return;
    }
  }

  const companyRef = doc(db, 'companies', request.companyId);
  const companySnap = await getDoc(companyRef);
  if (!companySnap.exists()) {
    throw new Error('Company not found.');
  }

  const nextModules = {
    ...normalizeActiveModules(companySnap.data()?.activeModules),
    [request.moduleKey]: true,
  };

  await updateDoc(companyRef, { activeModules: nextModules });
  await closePendingModuleRequests(
    db,
    request.companyId,
    request.moduleKey,
    MODULE_REQUEST_STATUS.GRANTED,
    resolver,
  );
}

/**
 * @param {import('firebase/firestore').Firestore} db
 * @param {{ id: string, companyId: string, moduleKey: string, ticketId?: string }} request
 * @param {{ uid?: string, email?: string }} resolver
 */
export async function rejectModuleRequest(db, request, resolver) {
  if (request.ticketId) {
    const ticketSnap = await getDoc(doc(db, 'tickets', request.ticketId));
    if (ticketSnap.exists()) {
      await resolveModuleRequestFromTicket(
        db,
        { id: ticketSnap.id, ...ticketSnap.data() },
        'rejected',
        resolver,
      );
      return;
    }
  }

  await closePendingModuleRequests(
    db,
    request.companyId,
    request.moduleKey,
    MODULE_REQUEST_STATUS.REJECTED,
    resolver,
  );
}

/**
 * @param {import('firebase/firestore').Timestamp | Date | string | null | undefined} value
 * @returns {string}
 */
export function formatModuleRequestDate(value) {
  if (!value) return '—';
  if (typeof value.toDate === 'function') {
    return value.toDate().toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}
